import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Star, Camera, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { resizeImageToDataUrl } from '../../utils/imageResize';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Review modal for /my-trips — same logic as the review page, plus photos.
 */
export const ReviewModal = ({ booking, open, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setRating(0); setHoverRating(0); setTitle(''); setComment(''); setPhotos([]);
    }
  }, [open, booking?.id]);

  const addPhotos = async (files) => {
    const remaining = 3 - photos.length;
    const picked = Array.from(files || []).slice(0, remaining);
    for (const f of picked) {
      try {
        const dataUrl = await resizeImageToDataUrl(f, 1200, 0.8);
        setPhotos((p) => (p.length < 3 ? [...p, dataUrl] : p));
      } catch (e) {
        toast.error(e.message);
      }
    }
  };

  const submit = async () => {
    if (!rating) { toast.error('Pick a star rating first'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/customer/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          booking_id: booking.id,
          rating,
          title: title || null,
          comment: comment || null,
          photos,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to submit review');
      toast.success(booking.has_reviewed ? 'Review updated!' : 'Thanks for your review!');
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle>{booking.has_reviewed ? 'Edit your review' : 'Leave a review'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-sm font-semibold text-gray-900">{booking.service_name}</p>
            <p className="text-xs text-gray-500">with {booking.organization?.name}</p>
          </div>

          <div className="flex justify-center gap-1.5" data-testid="review-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`review-star-${n}`}
                onMouseEnter={() => setHoverRating(n)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(n)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star className={`w-8 h-8 ${(hoverRating || rating) >= n ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
              </button>
            ))}
          </div>

          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            maxLength={120}
            data-testid="review-title-input"
          />
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="How was your experience?"
            rows={4}
            maxLength={2000}
            data-testid="review-comment-input"
          />

          {/* Photos */}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p} alt={`upload ${i + 1}`} className="w-16 h-16 rounded-xl object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-900 text-white flex items-center justify-center"
                    data-testid={`review-photo-remove-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < 3 && (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-16 h-16 rounded-xl bg-gray-50 hover:bg-gray-100 flex flex-col items-center justify-center text-gray-400 transition-colors"
                  data-testid="review-add-photo-btn"
                >
                  <Camera className="w-5 h-5" />
                  <span className="text-[10px] mt-0.5">Photo</span>
                </button>
              )}
            </div>
            <p className="text-[11px] text-gray-400 mt-1.5">Add up to 3 photos from your trip (optional)</p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
            />
          </div>

          <Button
            onClick={submit}
            disabled={loading}
            className="w-full h-11 bg-amber-500 hover:bg-amber-600 rounded-xl"
            data-testid="review-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (booking.has_reviewed ? 'Update review' : 'Submit review')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
